/**
 * `/auth/callback` — where the issuer puts us back down.
 *
 * Three things happen here and the order matters. The token is taken out of the URL, the
 * URL is *replaced* so the credential is not left in history or in whatever the user
 * copies out of the address bar next, and only then does navigation continue to the
 * `return_to` path — which was validated on the way out and is validated again here,
 * because a value that survived a round trip through a third party is a value that came
 * from a third party.
 */

import { Alert, AlertTitle, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from './context';
import { readCallback, safeReturnTo } from './redirect';
import { isExpired } from './token';

export function CallbackRoute() {
  const { acceptToken, signIn } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const result = readCallback();

    if (result.error) {
      setError(`The identity provider refused the sign-in: ${result.error}`);
      return;
    }
    if (!result.token) {
      setError(
        'The identity provider redirected here without a token. Check that this ' +
          "deployment's client_id and redirect_uri are registered with the issuer.",
      );
      return;
    }
    if (isExpired(result.token, 0)) {
      setError('The token handed back has already expired. Check the clock on this machine.');
      return;
    }

    acceptToken(result.token);
    // Replace, do not push: the callback URL still holds the token until this runs, and
    // Back must not be able to return to it.
    window.history.replaceState(null, '', window.location.pathname);
    navigate(safeReturnTo(result.returnTo), { replace: true });
  }, [acceptToken, navigate]);

  if (error) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', mt: 8, px: 3 }}>
        <Alert severity="error" variant="outlined">
          <AlertTitle sx={{ fontWeight: 700 }}>Sign-in did not complete</AlertTitle>
          <Typography sx={{ fontSize: 13, mb: 2 }}>{error}</Typography>
          <Button size="small" variant="contained" onClick={() => signIn('/items')}>
            Try again
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Stack alignItems="center" spacing={2} sx={{ mt: 12 }}>
      <CircularProgress size={28} />
      <Typography color="text.secondary" sx={{ fontSize: 13 }}>
        Completing sign-in…
      </Typography>
    </Stack>
  );
}
